import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, X as XIcon, Check, Sparkles, ShieldCheck, Play, Download, Presentation } from 'lucide-react';
import { sectorData } from '../sectorData';
import { sectorVideos } from '../videos';
import { sectorGalleries } from '../sectorGalleryData';
import SectorDemoGallery from '../components/SectorDemoGallery';

// Per-sector Synthesia pitch decks (Synthesia-ready PPTX)
const PITCH_DECK = {
  'nhs':           { file: '/synthesia/keystone-videra-nhs.pptx',             label: 'NHS Estates Portfolio',     size: '2.3 MB' },
  'hard-services': { file: '/synthesia/keystone-videra-fm-hardservices.pptx', label: 'Healthcare · FM Hard Services', size: '2.4 MB' },
  'construction':  { file: '/synthesia/keystone-videra-constructionplus.pptx',label: 'Construction+\u2122 · Tier-1', size: '2.3 MB' },
};

const V2SectorPage = () => {
  const { slug } = useParams();
  const s = sectorData[slug];
  if (!s) return <Navigate to="/sectors" replace />;
  const video = sectorVideos[slug];
  const gallery = sectorGalleries[slug];
  const deck = PITCH_DECK[slug];

  return (
    <div data-testid={`v2-sector-${slug}`}>
      {/* HERO with sector gradient */}
      <section className={`relative pt-20 pb-20 md:pt-28 md:pb-28 overflow-hidden`}>
        <div className={`absolute inset-0 bg-gradient-to-br ${s.gradient}`} />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 ${s.accentBg} border text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded backdrop-blur-sm`}>
              {s.tag}
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/80 font-semibold">{s.category}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-4xl">
            {s.title}
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-slate-200 leading-relaxed max-w-3xl">
            {s.intro}
          </p>

          {/* Demo CTA inline in hero */}
          <div className="mt-10 inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2 pl-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4 text-white/80" />
              <span className="text-white/90 font-medium">{s.workspaceName}</span>
              <span className="text-white/50">·</span>
              <span className="text-white/70">14-day trial</span>
            </div>
            <a
              href={s.demoUrl}
             
              className={`inline-flex items-center gap-2 bg-gradient-to-r ${s.buttonGradient} text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity group`}
              data-testid={`v2-sector-trial-${slug}`}
            >
              <Sparkles className="w-4 h-4" /> Start free trial <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Sector intro video — renders only when videoUrl is set in /v2/videos.js */}
      {video?.videoUrl && (
        <section className="py-16 md:py-20" data-testid={`v2-sector-video-${slug}`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Watch the intro</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mb-8">
              {s.workspaceName} in 60 seconds.
            </h2>
            <div className="relative aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <video
                src={video.videoUrl}
                poster={video.posterUrl || undefined}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-contain bg-black"
                data-testid={`v2-sector-video-player-${slug}`}
              />
            </div>
          </div>
        </section>
      )}

      {/* Demo workspace summary card */}
      <section className="py-20 md:py-24" data-testid="v2-sector-demo-summary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">The demo workspace</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {s.workspaceName}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {s.demoDescription}
              </p>
              <a
                href={s.demoUrl}
               
                className={`inline-flex items-center gap-2 bg-gradient-to-r ${s.buttonGradient} text-white px-6 py-3.5 rounded-lg font-medium hover:opacity-90 transition-opacity group`}
              >
                <Sparkles className="w-4 h-4" /> Try {s.workspaceName} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="text-xs text-slate-500 mt-3">14 days · no card · build up to 3 of your own sites</p>
              <p className="text-xs text-slate-600 mt-1">↗ Opens in a new tab · close it to return here.</p>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
              {s.demoStats.map((stat) => (
                <div key={stat} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3.5">
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${s.buttonGradient}`} />
                  <p className="text-sm text-slate-700">{stat}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pitch deck download — Synthesia-ready PPTX */}
      {deck && (
        <section className="py-14 md:py-16 bg-white" data-testid={`v2-sector-pitch-deck-${slug}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              transition={{ duration: 0.4 }}
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white p-6 md:p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-2 mb-3">
                    <Presentation className="w-4 h-4 text-blue-600" />
                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600">
                      Sector pitch deck · Synthesia-ready
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 leading-tight">
                    The Videra {deck.label} pitch deck.
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
                    7 slides, real product screenshots, visible voiceover panel on every slide and the
                    full script in the speaker notes. Drop it into Synthesia, pick an avatar, and you
                    have a narrated sector video in five minutes.
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> 7 slides · 16:9 · 1080p
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> Real product screenshots
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> ~70-second voiceover
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> PPTX · {deck.size}
                    </span>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <a
                    href={deck.file}
                    download
                    data-testid={`v2-sector-pitch-deck-download-${slug}`}
                    className="group block bg-slate-900 hover:bg-slate-800 text-white rounded-xl p-5 md:p-6 transition-colors shadow-[0_18px_40px_-16px_rgba(15,23,42,0.4)]"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                          Download · .pptx
                        </p>
                        <p className="mt-1 text-base md:text-lg font-medium truncate">
                          keystone-videra-{slug === 'hard-services' ? 'fm-hardservices' : slug === 'construction' ? 'constructionplus' : slug}.pptx
                        </p>
                        <p className="mt-1 text-xs text-slate-400">{deck.size} · 7 slides · Synthesia-ready</p>
                      </div>
                      <span className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 group-hover:bg-white/15 inline-flex items-center justify-center transition-colors">
                        <Download className="w-5 h-5" />
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Inside the demo — sector-specific screenshot gallery */}
      {gallery && (
        <SectorDemoGallery
          folder={gallery.folder}
          groups={gallery.groups}
          intro={gallery.intro}
          sectorName={s.workspaceName}
        />
      )}

      {/* Pain points vs Videra solutions */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">The reality today</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-8">Pain points</h2>
            <ul className="space-y-3">
              {s.painPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-4">
                  <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-3.5 h-3.5 text-red-500" />
                  </div>
                  <p className="text-sm text-slate-700">{p}</p>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 rounded-2xl p-8 lg:p-10 text-white"
          >
            <p className={`text-xs uppercase tracking-[0.2em] ${s.accentText} font-semibold mb-4`}>How Videra solves it</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-8">The Videra way</h2>
            <ul className="space-y-3">
              {s.howVidera.map((p) => (
                <li key={p} className="flex items-start gap-3 bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-200">{p}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={`py-24 md:py-32 bg-gradient-to-br ${s.gradient} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Start your 14-day trial of {s.workspaceName}.
          </h2>
          <p className="mt-6 text-lg text-slate-200">No card. Full product. Same code paths as our paying clients.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={s.demoUrl}
             
              className={`inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 font-medium tracking-wide hover:bg-slate-100 transition-all rounded-md group`}
            >
              <Sparkles className="w-4 h-4" /> Start free trial <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center bg-transparent border border-white/40 text-white px-8 py-4 font-medium tracking-wide hover:bg-white/10 transition-all rounded-md">
              Talk to a specialist
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default V2SectorPage;
